/**
 * Encapsulates all react-dnd logic for a section, including being a drag source
 * for reordering and a drop target for both sections and controls.
 */
import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

export const useSectionDnd = ({ section, index, moveSection, moveControl, setDropIndicator, activePageId }) => {
    const ref = useRef(null);

    // Hook for dragging the section itself
    const [{ isDragging }, drag] = useDrag({
        type: 'SECTION',
        item: { id: section.id, index },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    // Hook for dropping other sections onto this one to reorder
    const [, sectionDrop] = useDrop({
        accept: 'SECTION',
        hover(item) {
            if (!ref.current || item.index === index) return;
            moveSection(activePageId, item.index, index);
            item.index = index; // Mutate the item in flight to avoid index issues
        },
    });

    // Hook for dropping controls into this section's content area
    const [{ isOverDropArea }, controlDrop] = useDrop({
        accept: ['CONTROL', 'MOBILE_OBJECT'],
        hover(_, monitor) {
            if (section.controls.length === 0 && monitor.canDrop()) {
                setDropIndicator({ targetId: section.id, position: 'inside' });
            }
        },
        drop(item, monitor) {

            if (monitor.didDrop()) {
                return;
            }

            moveControl({
                pageId: activePageId,
                controlId: item.id,
                fromSectionId: item.sectionId,
                fromIndex: item.index,
                toSectionId: section.id,
                toIndex: section.controls.length,
            });
            setDropIndicator(null);
        },
        leave() {
            setDropIndicator(null);
        },
        collect: (monitor) => ({
            isOverDropArea: monitor.canDrop() && monitor.isOver({ shallow: true }),
        }),
    });

    // Chain the refs together: the main ref is both a drag source and a drop target
    drag(sectionDrop(ref));

    return { ref, isDragging, controlDrop, isOverDropArea };
};